import { ReactNode, useCallback, useMemo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// Types
import CaseStatuses from "app/types/CaseStatuses";
// Store
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { getPendingCases, getInsuranceCases } from 'app/store/Cases/Cases.async';
// Actions
import { CasesActions } from 'app/store/Cases/Cases.slice';
// Selectors
import { selectCurrentAccount } from 'app/store/Accounts/Accounts.selectors';
// Utils
import { getSearchParam } from 'app/utilities/Utilities';
import { Context } from '../utils/useCasesContext';
// Service
import LocalStorageService from "app/services/LocalStorage.service";
// Constants
import { DATA_LIMIT } from 'app/App.constants';

type Props = {
  children: ReactNode;
};

const initialParams = {
  limit: DATA_LIMIT,
  offset: 0,
  status: CaseStatuses.Open,
  search: '',
  clientAccountId: null,
  'invoice.status': 'all',
  'sort': 'createdOn desc',
  'sort.priority.collectionIds': ''
};

const CasesProvider = ({ children }:Props) => {
  const { search } = useLocation<{ search:string }>();

  const activeTab = getSearchParam(search, 'tab') as CaseStatuses;

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const currentAccount = useAppSelector(selectCurrentAccount);

  const [ params, setParams ] = useState(initialParams);

  const isNotOpenOrArchived = useMemo(() => {
    return [CaseStatuses.Pending, CaseStatuses.Waiting, CaseStatuses.Rejected].includes(activeTab);
  }, [activeTab]);

  const accountParams = useMemo(() => {
    if ( !currentAccount ) return null;

    const casesSorting = LocalStorageService.getCasesSorting(currentAccount.id);
    const sort = isNotOpenOrArchived ? 'createdOn desc' : casesSorting['sort'] || 'createdOn desc';
    const sortPriorityCollectionIds = isNotOpenOrArchived ? '' : casesSorting['sort.priority.collectionIds'] || '';

    return {
      ...params,
      status: activeTab || CaseStatuses.Open,
      sort,
      'sort.priority.collectionIds': sortPriorityCollectionIds
    };
  }, [currentAccount, params, activeTab, isNotOpenOrArchived])

  const handleChangeParams = useCallback((newParams:Partial<typeof params>) => {
    setParams({
      ...params,
      ...newParams
    });
  }, [params]);

  useEffect(() => {
    dispatch(getPendingCases({}));
    return () => {
      dispatch(CasesActions.resetCasesList());
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if ( !accountParams ) return;

    dispatch(getInsuranceCases(accountParams));
  }, [dispatch, accountParams]);

  if ( !accountParams ) return null;
  return (
    <Context.Provider value={{
      params: accountParams,
      onChangeParams: handleChangeParams
    }}>{children}</Context.Provider>
  )
}

export default CasesProvider